import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";

import {
  isBlockVisible,
  robotColor,
  useRobot,
  useRobotAnalytics,
  type RobotDetail,
  type RobotPricing,
  type ShowcasePeriod,
} from "@/entities/robot";
import { useMyRobot } from "@/entities/user-robot";
import { useWallet } from "@/entities/wallet";
import { ApiError } from "@/shared/api";
import { ROUTES } from "@/shared/config";
import { formatPercent, moneySign } from "@/shared/lib";
import { Segmented } from "@/shared/ui";
import { EquityChart } from "@/widgets/equity-chart";
import { CapitalHistoryList, MyResultSummary, RunningStateBlock } from "@/widgets/my-result";
import { TopupSheet } from "@/widgets/topup-sheet";
import {
  AiSignalsGrid,
  AudienceBlock,
  Disclaimer,
  KeyMetricsBlock,
  MarketsBlock,
  PricingBlock,
  RecentTradesList,
  RobotHero,
  RobotNowBlock,
  StatCards,
  TextBlock,
} from "@/widgets/robot-analytics";
import { RobotComments } from "@/widgets/robot-comments";
import { RobotLeaderboard } from "@/widgets/robot-leaderboard";
import { ScreenSkeleton, ScreenState } from "@/widgets/screen-state";
import { ShowcaseChart } from "@/widgets/showcase-chart";

/**
 * Экран робота — полная аналитика стратегии (ТЗ §5–§17).
 *
 * Роут не менялся: `/robots/:id` и есть «Открыть аналитику» с карточки
 * каталога, поэтому переход общим элементом (FE-13) и все существующие ссылки
 * продолжают работать.
 *
 * Два запроса, а не один. `useRobot` даёт личность робота и живёт под ключом без
 * периода — на него завязан переход из каталога и покупка. `useRobotAnalytics`
 * зависит от периода графика, и держать личность робота в ключе с периодом
 * значило бы перезапрашивать шапку при каждом нажатии на «3М».
 *
 * Период — состояние экрана, а не URL: как и фильтр рынка в каталоге. Возврат с
 * экрана покупки не должен восстанавливать чужой период, а делиться ссылкой на
 * «Pulse за полгода» продукт не предполагает.
 */
const DEFAULT_PERIOD: ShowcasePeriod = "1y";

/** Что показывает экран: витрину стратегии или собственный результат (ТЗ §21). */
type View = "strategy" | "mine";

export function RobotDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const robotId = Number(id);
  const [period, setPeriod] = useState<ShowcasePeriod>(DEFAULT_PERIOD);
  const [view, setView] = useState<View>("strategy");
  const [toppingUp, setToppingUp] = useState(false);

  const robot = useRobot(robotId);
  const analytics = useRobotAnalytics(robotId, period);
  // Хуки до ранних возвратов: порядок хуков обязан быть неизменным, а покупки
  // у пользователя может и не быть. `-1` — заведомо несуществующий id, запрос
  // при этом выключен флагом.
  const mine = robot.data?.my_user_robot_id ?? null;
  const myRobot = useMyRobot(mine ?? -1, "90d", mine !== null);
  const wallet = useWallet();

  if (!Number.isFinite(robotId)) {
    return (
      <ScreenState
        tone="info"
        icon="questionMark"
        title={t("robots.notFound.title")}
        body={t("robots.notFound.body")}
      />
    );
  }

  if (robot.isPending) {
    return <ScreenSkeleton blocks={[338, 240, 170, 134, 134, 50]} />;
  }

  if (robot.isError) {
    // Чужой или несуществующий робот приходит как 404, а не 403: владение
    // ресурсом бэкенд не раскрывает.
    const notFound = robot.error instanceof ApiError && robot.error.code === "NOT_FOUND";
    return (
      <ScreenState
        tone={notFound ? "info" : "loss"}
        icon={notFound ? "questionMark" : "warningFill"}
        title={t(notFound ? "robots.notFound.title" : "states.error.title")}
        body={t(notFound ? "robots.notFound.body" : "states.error.body")}
        {...(notFound
          ? {}
          : {
              action: {
                label: t("states.error.action"),
                onClick: () => {
                  void robot.refetch();
                  void analytics.refetch();
                },
              },
            })}
      />
    );
  }

  const data = robot.data;
  // Аналитика падает отдельно от карточки (доработки §4.2): пока карточка
  // пришла, экран обязан показать ключевые метрики и лимитированное
  // предложение, а на месте графика и производных блоков написать «Данные
  // обновляются» — но не нули. Нулевая статистика на месте недоступного
  // сервиса это не деградация, а ложное утверждение о роботе.
  const report = analytics.data;
  const color = robotColor(data);
  const blocks = data.visible_blocks;
  const owned = myRobot.data;

  // Переключатель появляется только у владельца: без покупки показывать
  // «Мой результат» нечем (ТЗ §21).
  const toggle =
    owned === undefined ? null : (
      <Segmented
        ariaLabel={t("robots.view.label")}
        variant="solid"
        value={view}
        onChange={setView}
        options={[
          { value: "strategy", label: t("robots.view.strategy") },
          { value: "mine", label: t("robots.view.mine") },
        ]}
      />
    );

  if (owned !== undefined && view === "mine") {
    return (
      <div className="flex flex-col gap-3 px-5 py-3">
        <header className="flex h-[42px] items-center gap-2">
          <button
            type="button"
            onClick={() => void navigate(-1)}
            data-haptic="nav"
            aria-label={t("common.back")}
            className="press text-h1 font-medium text-text-primary"
          >
            ‹
          </button>
          <h1 className="text-h2 font-semibold text-text-primary">{data.name}</h1>
        </header>

        {toggle}

        <RunningStateBlock robot={owned} onTopup={() => setToppingUp(true)} />
        <MyResultSummary robot={owned} />

        {/*
          График строится из начислений и показывает ТОЛЬКО торговый результат:
          пополнение в `daily_results` не пишется вовсе (ТЗ §22). Внесённые
          средства видны отдельно — в истории капитала ниже.
        */}
        <EquityChart
          points={owned.chart}
          total={owned.earned_total}
          totalPct={owned.earned_pct}
          period={owned.chart_period}
          onPeriodChange={() => undefined}
          busy={myRobot.isFetching}
        />

        <CapitalHistoryList entries={owned.capital_history} />

        {toppingUp && wallet.data !== undefined ? (
          <TopupSheet
            robot={owned}
            balance={wallet.data.balance}
            onClose={() => setToppingUp(false)}
          />
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 px-5 py-3">
      <header className="flex h-[42px] items-center gap-2">
        <button
          type="button"
          onClick={() => void navigate(-1)}
          data-haptic="nav"
          aria-label={t("common.back")}
          className="press text-h1 font-medium text-text-primary"
        >
          ‹
        </button>
        <h1 className="text-h2 font-semibold text-text-primary">{data.name}</h1>
      </header>

      {toggle}

      {/*
        Лесенка процентов берётся из КАРТОЧКИ, а не из аналитики. Бэкенд
        собирает `RobotCard.performance` и `RobotAnalytics.returns` одним
        расчётом из одного ряда, поэтому они равны по построению — а экран за
        это получает шапку, которая не зависит от тяжёлого запроса и переживает
        его недоступность (доработки §4.2).
      */}
      <RobotHero robot={data} returns={data.performance} />
      {/* Сразу под шапкой: ключевые метрики §4.1 — сколько людей, сколько
          заработали, сколько под управлением, доходность с периодом и методом
          расчёта, риск с пояснением и период работы. Всё из карточки, поэтому
          блок появляется вместе с шапкой и совпадает с каталогом. */}
      <KeyMetricsBlock
        robot={data}
        returns={data.performance}
        showAum={isBlockVisible(blocks, "aum")}
      />
      <Disclaimer text={data.disclaimer} />

      {report === undefined ? (
        <AnalyticsUnavailable
          pending={analytics.isPending}
          onRetry={() => void analytics.refetch()}
        />
      ) : (
        <>
          <ShowcaseChart
            name={data.name}
            color={color}
            points={report.chart}
            period={period}
            onPeriodChange={setPeriod}
            busy={analytics.isFetching}
          />

          <StatCards stats={report.stats} showWinRate={isBlockVisible(blocks, "win_rate")} />
          <MarketsBlock markets={report.markets} color={color} />
          {isBlockVisible(blocks, "now") ? (
            <RobotNowBlock name={data.name} state={report.now} />
          ) : null}

          {isBlockVisible(blocks, "trades") ? <RecentTradesList trades={report.trades} /> : null}
        </>
      )}

      <Disclaimer text={data.disclaimer} />

      <TextBlock
        title={t("robots.analytics.strategy.title")}
        body={data.about_strategy ?? data.how_it_works}
      />
      <TextBlock title={t("robots.howItWorks")} body={data.how_it_works} />
      <TextBlock title={t("robots.analytics.ladder.title")} body={data.ladder_note} />

      {/* Набор сигналов — свойство робота, и он есть в карточке: аналитика
          повторяет его тем же списком. Берём из карточки, чтобы блок пережил
          её недоступность. */}
      <AiSignalsGrid signals={data.ai_signals} core={data.code === "pulse"} />
      <TradingFacts robot={data} />
      <FeeNote robot={data} />

      {/* Счётчик пользователей и суммарный результат живут в блоке ключевых
          метрик под шапкой — здесь остаются средний срок и распределение.
          Показывать те же цифры дважды на одном экране значило бы заставить
          сверять, не разошлись ли они. */}
      {report !== undefined ? (
        <AudienceBlock
          audience={report.audience}
          showDistribution={isBlockVisible(blocks, "distribution")}
          showAum={false}
          showHeadcount={false}
          showProfit={false}
        />
      ) : null}

      {/* ТОП-3 пользователей (доработки §5). Отдельным блоком под аудиторией:
          это витрина людей, а не цифр робота. Оператор гасит его тем же
          флагом, что и раньше. */}
      {report !== undefined && isBlockVisible(blocks, "top_users") ? (
        <RobotLeaderboard audience={report.audience} />
      ) : null}

      {/* Отзывы (доработки §6). Под подиумом и над ценой намеренно: сначала
          человек видит, кто и с каким результатом пользуется роботом, потом —
          что они об этом пишут, и только потом кнопку запуска.

          `data.comments === null` означает «раздел выключен настройкой», а не
          «отзывов нет»: пустая сводка с нулями сообщала бы неправду. */}
      {data.comments !== null && data.comments !== undefined && isBlockVisible(blocks, "comments") ? (
        <RobotComments robotId={data.id} summary={data.comments} />
      ) : null}

      {/*
        Запущенный робот не предлагает запуститься второй раз: капитал
        наращивается пополнением (ТЗ §18), а две покупки одного робота дали бы
        две истории и два ROI там, где пользователь ждёт одну цифру.

        Условия сделки собираются из карточки, когда аналитика недоступна:
        кнопка запуска — последнее, что имеет право пропасть с экрана из-за
        недоступного сервиса статистики.
      */}
      {owned === undefined ? (
        <PricingBlock
          name={data.name}
          pricing={report?.pricing ?? pricingFromCard(data)}
          status={data.status}
          cta={data.cta_text}
          offer={data.limited}
          onLaunch={() => void navigate(ROUTES.robotBuy(data.id))}
        />
      ) : (
        <RunningStateBlock robot={owned} onTopup={() => setToppingUp(true)} />
      )}

      {toppingUp && owned !== undefined && wallet.data !== undefined ? (
        <TopupSheet
          robot={owned}
          balance={wallet.data.balance}
          onClose={() => setToppingUp(false)}
        />
      ) : null}
    </div>
  );
}

/**
 * «Как торгует» — биржи, пары и режим работы робота.
 *
 * Незаполненное поле строку не рисует: оператор заводит робота в админке
 * постепенно, и пустое «Плечо: —» выглядело бы поломкой, а не пробелом.
 * Если не заполнено ничего, блок не показывается целиком.
 */
function TradingFacts({ robot }: { robot: RobotDetail }) {
  const { t } = useTranslation();

  const rows: { label: string; value: string }[] = [
    { label: t("robots.trading.exchanges"), value: robot.exchanges.join(" · ") },
    { label: t("robots.trading.pairs"), value: robot.pairs.join(" · ") },
    { label: t("robots.trading.leverage"), value: robot.leverage ?? "" },
    { label: t("robots.trading.timeframe"), value: robot.timeframe ?? "" },
    { label: t("robots.trading.tradesPerDay"), value: robot.trades_per_day ?? "" },
    { label: t("robots.trading.avgHold"), value: robot.avg_hold ?? "" },
  ].filter((row) => row.value !== "");

  if (rows.length === 0) return null;

  return (
    <section className="flex flex-col gap-2 rounded-[20px] border border-border-subtle bg-background-elevated p-4">
      <h2 className="text-body font-semibold text-text-primary">{t("robots.trading.title")}</h2>
      <dl className="flex flex-col gap-1.5">
        {rows.map((row) => (
          <div key={row.label} className="flex items-start justify-between gap-3 text-caption">
            <dt className="font-normal text-text-secondary">{row.label}</dt>
            <dd className="min-w-0 text-right font-semibold text-text-primary">{row.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/** Комиссия — условие сделки, и прятать её до экрана подтверждения нельзя. */
function FeeNote({ robot }: { robot: RobotDetail }) {
  const { t } = useTranslation();
  // Сравнение с нулём, а не вычисление: правило ТЗ №5 не нарушается.
  const charged = moneySign(robot.profit_fee_pct) > 0;

  return (
    <section className="flex flex-col gap-2 rounded-[20px] border border-border-subtle bg-background-elevated p-4">
      <h2 className="text-body font-semibold text-text-primary">
        {charged
          ? t("robots.fee", { pct: formatPercent(robot.profit_fee_pct) })
          : t("robots.feeNone")}
      </h2>
      <p className="text-caption font-normal text-text-secondary">
        {charged
          ? t("robots.feeBody", { pct: formatPercent(robot.profit_fee_pct) })
          : t("robots.feeNoneBody")}
      </p>
    </section>
  );
}


/**
 * Состояние «Данные обновляются» на месте блоков аналитики (доработки §4.2).
 *
 * Требование прямое: «при временной недоступности analytics service показывать
 * „Данные обновляются“, а не 0, чтобы не создавать ложную статистику». Нулевая
 * просадка и «сделок за год: 0» на месте упавшего сервиса — это не пустой
 * экран, а неверное утверждение о роботе.
 *
 * Пока запрос в пути — скелетон: писать «обновляются» на первой загрузке
 * значило бы объявлять поломкой обычное ожидание.
 */
function AnalyticsUnavailable({
  pending,
  onRetry,
}: {
  pending: boolean;
  onRetry: () => void;
}) {
  const { t } = useTranslation();

  if (pending) return <ScreenSkeleton blocks={[240, 170, 134]} />;

  return (
    <ScreenState
      tone="info"
      icon="chartLineUp"
      title={t("states.updating.title")}
      body={t("states.updating.body")}
      action={{ label: t("states.updating.action"), onClick: onRetry }}
    />
  );
}

/**
 * Условия сделки из карточки — запасной источник для `PricingBlock`.
 *
 * Все четыре поля есть и в `RobotDetail`, и в `RobotAnalytics.pricing`, и
 * заполняются на бэкенде из одних и тех же колонок робота. Собрать их здесь
 * дешевле, чем прятать кнопку запуска, когда недоступна статистика.
 */
function pricingFromCard(robot: RobotDetail): RobotPricing {
  return {
    min_amount: robot.min_amount,
    recommended_amount: robot.recommended_amount,
    profit_fee_pct: robot.profit_fee_pct,
    includes: robot.tier_includes,
  };
}
